import { DAILY_BONUS } from '../../data/daily';
import { claimDaily, dailyBonus, dailyQuests, dailyStreak } from '../../systems/daily';
import { button, meter } from '../../../ui/components';
import { h } from '../../../ui/dom';
import { doodleSvg } from '../../../ui/doodleSvg';
import { t, tn } from '../../../i18n';
import type { NotebookCtx } from './tabs';

/** "Today's Quests": three fresh quests a day, a bonus for all three and a streak. */
export function dailySection(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  const quests = dailyQuests(save);
  const streak = dailyStreak(save);
  const allDone = quests.length > 0 && quests.every((q) => q.claimed);
  // The bonus that finishing today would pay (today's streak continues yesterday's).
  const nextBonus = dailyBonus(allDone ? streak : streak + 1);
  return h(
    'section',
    { class: 'daily-quests' },
    h('h3', { class: 'section-title' }, t("Today's Quests")),
    h('p', { class: 'hand-hint' }, t('New quests every day. Finish all three for a bonus.')),
    h(
      'ol',
      { class: 'quest-list daily-list' },
      quests.map(({ def, progress, claimed, ready }) =>
        h(
          'li',
          { class: `quest ${claimed ? 'claimed' : ''} ${ready ? 'ready' : ''}`, 'data-daily': def.id },
          h('span', { class: 'checkbox', 'aria-hidden': 'true' }, claimed ? doodleSvg('check') : null),
          h('div', { class: 'quest-body' }, h('b', null, t(def.title)), h('p', null, t(def.note)), meter(progress, def.goal, 'exp', `${progress} / ${def.goal}`)),
          h(
            'div',
            { class: 'quest-reward' },
            h('small', null, t('{exp} EXP · {coins} coins', { exp: def.exp, coins: def.coins })),
            ready
              ? button(t('Claim!'), {
                  small: true,
                  variant: 'primary',
                  color: 'yellow',
                  sfx: app,
                  onClick: () =>
                    app.store.update(['quests', 'hero', 'inventory', 'memories'], (s) => {
                      const done = claimDaily(s, app.store.hero, def.id);
                      if (!done) return;
                      app.sfx.play('win');
                      app.toast(
                        done.bonus
                          ? t('All done for today! +{coins} bonus coins · {n}-day streak', { coins: done.bonus.coins, n: done.bonus.streak }) +
                              (done.bonus.tickets ? ` · ${t('+1 fortune ticket')}` : '')
                          : t('Quest complete: {name}!', { name: t(def.title) }),
                        'success',
                      );
                    }),
                })
              : null,
          ),
        ),
      ),
    ),
    h(
      'p',
      { class: `daily-bonus sticky-note ${allDone ? 'green' : 'yellow'}` },
      allDone
        ? t('All done today! Come back tomorrow for new quests.')
        : t('Bonus for all three: +{coins} coins', { coins: nextBonus }),
      ' · ',
      streak ? tn(streak, '{n}-day streak', '{n}-day streak') : t('Start a streak today!'),
      streak >= DAILY_BONUS.maxStreakDays ? ` ${t('(max bonus)')}` : '',
    ),
  );
}
